import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/brand/Logo";

const input = "w-full h-11 px-3 rounded-lg border border-input bg-white focus:outline-none focus:ring-2 focus:ring-electric text-sm";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token");
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!token) { setError("No invitation token provided."); return; }
    api.get(`/invitations/validate?token=${encodeURIComponent(token)}`)
      .then((r) => { setInvite(r.data); setForm((f) => ({ ...f, name: r.data.name || "" })); })
      .catch((e) => setError(formatApiError(e.response?.data?.detail)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/invitations/accept", { token, password: form.password, name: form.name });
      toast.success("Account created! Please log in.");
      nav("/owner/login");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8"><Logo /></div>
        <div className="rounded-2xl bg-white border border-slate-200 p-6">
          <div className="flex items-center gap-2 text-electric mb-1"><ShieldCheck className="h-5 w-5" /><span className="text-xs font-mono uppercase tracking-wider">Staff Invitation</span></div>
          {error ? (
            <div data-testid="invite-error">
              <h1 className="font-heading text-xl font-bold text-navy">Invitation unavailable</h1>
              <p className="mt-3 text-sm text-slate-600">{error}</p>
              <Link to="/owner/login" className="block text-center text-sm text-electric mt-6">Back to login</Link>
            </div>
          ) : !invite ? (
            <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-electric" /></div>
          ) : (
            <>
              <h1 className="font-heading text-xl font-bold text-navy">Set up your account</h1>
              <p className="mt-2 text-sm text-slate-600">You're joining as <b className="capitalize">{invite.role.replace("_", " ")}</b>.</p>
              <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-500" data-testid="invite-email">{invite.email}</div>
              <form onSubmit={submit} className="mt-4 space-y-4" data-testid="accept-form">
                <input className={input} placeholder="Your full name" required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="accept-name" />
                <input className={input} type="password" placeholder="Create a password (min 8 chars)" required minLength={8} value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="accept-password" />
                <Button type="submit" disabled={loading} className="w-full bg-electric hover:bg-tech" data-testid="accept-submit">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}</Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
